import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle2, XCircle, HelpCircle, Clock, Bell, Settings, ChevronDown, ChevronUp, MapPin, CalendarDays, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AvailabilityPanel({ orgId, teams, userRole, familyId }) {
  const { isDark } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('upcoming');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamMatrix, setTeamMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [config, setConfig] = useState({});
  const [noteText, setNoteText] = useState('');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', event_type: 'practice', date: '', start_time: '', end_time: '', location: '', team_id: '' });
  const [addingEvent, setAddingEvent] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringEnd, setRecurringEnd] = useState('');
  const [recurringDays, setRecurringDays] = useState([]);

  const canManage = ['ad', 'scheduler', 'company_support'].includes(userRole);

  useEffect(() => { if (orgId) fetchEvents(); }, [orgId]);
  useEffect(() => {
    if (teams?.length > 0 && !selectedTeamId) setSelectedTeamId(teams[0]?.id);
  }, [teams]);
  useEffect(() => {
    if (selectedTeamId && view === 'team') fetchTeamMatrix();
  }, [selectedTeamId, view]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/events`, { withCredentials: true });
      setEvents(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchTeamMatrix = async () => {
    if (!selectedTeamId) return;
    setMatrixLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/team/${selectedTeamId}`, { withCredentials: true });
      setTeamMatrix(res.data);
    } catch (err) { console.error(err); }
    finally { setMatrixLoading(false); }
  };

  const fetchEventDetail = async (eventId) => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/event/${eventId}/summary`, { withCredentials: true });
      setEventDetail(res.data);
    } catch (err) { console.error(err); }
  };

  const respond = async (eventId, playerId, status) => {
    try {
      await axios.post(`${API_URL}/api/availability/${orgId}/respond`, {
        player_id: playerId, event_id: eventId, status, team_id: selectedTeamId || '', note: noteText
      }, { withCredentials: true });
      fetchEvents();
      if (expandedEvent === eventId) fetchEventDetail(eventId);
      if (view === 'team') fetchTeamMatrix();
      setNoteText('');
    } catch (err) { console.error(err); }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/config`, { withCredentials: true });
      setConfig(res.data);
    } catch (err) { console.error(err); }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/availability/${orgId}/config`, config, { withCredentials: true });
    } catch (err) { console.error(err); }
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.date) return;
    setAddingEvent(true);
    try {
      if (isRecurring && recurringEnd && recurringDays.length > 0) {
        await axios.post(`${API_URL}/api/availability/${orgId}/events/recurring`, {
          ...newEvent,
          team_id: newEvent.team_id || (teams?.[0]?.id),
          start_date: newEvent.date,
          end_date: recurringEnd,
          days_of_week: recurringDays,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/availability/${orgId}/events`, {
          ...newEvent, team_id: newEvent.team_id || (teams?.[0]?.id)
        }, { withCredentials: true });
      }
      setShowAddEvent(false);
      setNewEvent({ title: '', event_type: 'practice', date: '', start_time: '', end_time: '', location: '', team_id: '' });
      setIsRecurring(false); setRecurringEnd(''); setRecurringDays([]);
      fetchEvents();
    } catch (err) { console.error(err); }
    finally { setAddingEvent(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API_URL}/api/availability/${orgId}/events/${eventId}`, { withCredentials: true });
      fetchEvents();
    } catch (err) { console.error(err); }
  };

  const EVENT_CATEGORY_COLORS = {
    game: 'bg-blue-600', practice: 'bg-green-600', meeting: 'bg-purple-600', fundraiser: 'bg-amber-600', other: 'bg-slate-500',
  };

  const toggleEventDetail = (eventId) => {
    if (expandedEvent === eventId) { setExpandedEvent(null); setEventDetail(null); }
    else { setExpandedEvent(eventId); fetchEventDetail(eventId); }
  };

  const StatusBtn = ({ status, current, onClick, icon: Icon, label, color }) => (
    <Button size="sm" variant={current === status ? 'default' : 'outline'}
      onClick={onClick}
      className={current === status ? `${color} text-white` : isDark ? 'border-slate-600 text-slate-300' : ''}
      data-testid={`avail-${status}`}
    >
      <Icon className="h-3 w-3 mr-1" /> {label}
    </Button>
  );

  return (
    <div className="space-y-4" data-testid="availability-panel">
      <div className="flex gap-2 flex-wrap">
        {['upcoming', 'team', ...(canManage ? ['settings'] : [])].map(v => (
          <Button key={v} size="sm" variant={view === v ? 'default' : 'outline'}
            onClick={() => { setView(v); if (v === 'settings') fetchConfig(); }}
            className={view === v ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}
            data-testid={`availability-view-${v}`}
          >
            {v === 'upcoming' && 'Upcoming Events'}
            {v === 'team' && 'Team Matrix'}
            {v === 'settings' && 'Settings'}
          </Button>
        ))}
      </div>

      {/* UPCOMING EVENTS VIEW */}
      {view === 'upcoming' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <CheckCircle2 className="h-5 w-5 text-green-500" /> Availability Tracker
                <Badge variant="outline" className="ml-2 text-xs">{events.length} events</Badge>
              </CardTitle>
              {canManage && (
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => setShowAddEvent(true)} data-testid="add-event-btn">
                  <Plus className="h-3 w-3 mr-1" />Add Event
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> : events.length > 0 ? (
              <div className="space-y-2">
                {events.map((event) => {
                  const summary = event.availability_summary || {};
                  const cat = event.event_category || 'game';
                  return (
                    <div key={event.id} className={`rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => toggleEventDetail(event.id)}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-white text-[10px] ${EVENT_CATEGORY_COLORS[cat] || 'bg-slate-500'}`}>{cat.toUpperCase()}</Badge>
                            {event.repeating && <Badge className="bg-cyan-600/20 text-cyan-400 text-[10px]">Recurring</Badge>}
                            <CalendarDays className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{event.date}</span>
                            {cat === 'game' ? (
                              <>
                                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>vs</span>
                                <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{event.opponent || 'TBD'}</span>
                                <Badge variant="outline" className="text-xs">{event.home_away}</Badge>
                              </>
                            ) : (
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{event.title || event.opponent || ''}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{event.location || 'TBD'}</span>
                            {event.start_time && <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>· {event.start_time}{event.end_time && ` - ${event.end_time}`}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex gap-2 text-xs">
                            <span className="text-green-500 font-medium">{summary.yes || 0} yes</span>
                            <span className="text-yellow-500 font-medium">{summary.maybe || 0} maybe</span>
                            <span className="text-red-500 font-medium">{summary.no || 0} no</span>
                          </div>
                          {cat !== 'game' && canManage && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }} data-testid={`delete-event-${event.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                          {event.repeating && event.series_id && canManage && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-orange-500" onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.delete(`${API_URL}/api/availability/${orgId}/events/series/${event.series_id}`, { withCredentials: true });
                                fetchEvents();
                              } catch (err) { console.error(err); }
                            }} data-testid={`delete-series-${event.series_id}`}>
                              Delete Series
                            </Button>
                          )}
                          {expandedEvent === event.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                      {expandedEvent === event.id && eventDetail && (
                        <div className={`px-3 pb-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                          <div className="mt-3 space-y-2">
                            {eventDetail.responses?.length > 0 ? eventDetail.responses.map((r, i) => (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{r.player_name}</span>
                                <div className="flex items-center gap-2">
                                  {r.note && <span className={`text-xs italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{r.note}</span>}
                                  <Badge className={r.status === 'yes' ? 'bg-green-500/10 text-green-600' : r.status === 'maybe' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-red-500/10 text-red-600'}>
                                    {r.status}
                                  </Badge>
                                </div>
                              </div>
                            )) : <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No responses yet</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No Upcoming Events</p>
                <p className="text-sm mt-1">Games will appear here when scheduled</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* TEAM MATRIX VIEW */}
      {view === 'team' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>Team Availability Matrix</CardTitle>
              {teams && teams.length > 1 && (
                <select value={selectedTeamId || ''} onChange={e => setSelectedTeamId(e.target.value)}
                  className={`text-sm rounded px-2 py-1 border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                  data-testid="team-matrix-select">
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {matrixLoading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading matrix...</p> :
             teamMatrix && teamMatrix.events?.length > 0 && teamMatrix.players?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={`w-full text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <thead>
                    <tr className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                      <th className="text-left p-2 sticky left-0 bg-inherit">Player</th>
                      {teamMatrix.events.slice(0, 10).map(ev => (
                        <th key={ev.id} className="p-2 text-center text-xs min-w-[80px]">
                          <div>{ev.date}</div>
                          <div className="font-normal text-xs opacity-60">{ev.opponent?.substring(0, 10)}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teamMatrix.players.map(player => (
                      <tr key={player.id} className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                        <td className="p-2 sticky left-0 bg-inherit whitespace-nowrap">
                          {player.jersey_number && <span className="text-xs font-mono mr-1">#{player.jersey_number}</span>}
                          {player.first_name} {player.last_name}
                        </td>
                        {teamMatrix.events.slice(0, 10).map(ev => {
                          const resp = teamMatrix.responses?.[player.id]?.[ev.id];
                          const st = resp?.status;
                          return (
                            <td key={ev.id} className="p-1 text-center">
                              <div className="flex gap-0.5 justify-center">
                                <button onClick={() => respond(ev.id, player.id, 'yes')} title="Yes"
                                  className={`w-6 h-6 rounded text-xs flex items-center justify-center ${st === 'yes' ? 'bg-green-500 text-white' : isDark ? 'bg-slate-700 text-slate-400 hover:bg-green-800' : 'bg-slate-100 hover:bg-green-100'}`}>
                                  <CheckCircle2 className="h-3 w-3" />
                                </button>
                                <button onClick={() => respond(ev.id, player.id, 'maybe')} title="Maybe"
                                  className={`w-6 h-6 rounded text-xs flex items-center justify-center ${st === 'maybe' ? 'bg-yellow-500 text-white' : isDark ? 'bg-slate-700 text-slate-400 hover:bg-yellow-800' : 'bg-slate-100 hover:bg-yellow-100'}`}>
                                  <HelpCircle className="h-3 w-3" />
                                </button>
                                <button onClick={() => respond(ev.id, player.id, 'no')} title="No"
                                  className={`w-6 h-6 rounded text-xs flex items-center justify-center ${st === 'no' ? 'bg-red-500 text-white' : isDark ? 'bg-slate-700 text-slate-400 hover:bg-red-800' : 'bg-slate-100 hover:bg-red-100'}`}>
                                  <XCircle className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <p>No roster data or upcoming events for this team</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SETTINGS VIEW */}
      {view === 'settings' && canManage && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Settings className="h-5 w-5" /> Availability Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Cutoff Hours Before Event</Label>
              <Input type="number" value={config.cutoff_hours || 0}
                onChange={e => setConfig({...config, cutoff_hours: parseInt(e.target.value) || 0})}
                className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''}
                data-testid="config-cutoff" />
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>0 = no cutoff</p>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Reminder Hours Before Event</Label>
              <Input type="number" value={config.reminder_hours_before || 24}
                onChange={e => setConfig({...config, reminder_hours_before: parseInt(e.target.value) || 24})}
                className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''}
                data-testid="config-reminder-hours" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={config.notes_private || false}
                  onChange={e => setConfig({...config, notes_private: e.target.checked})} />
                <span className={`text-sm ${isDark ? 'text-slate-300' : ''}`}>Notes visible to admins only</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={config.reminder_enabled || false}
                  onChange={e => setConfig({...config, reminder_enabled: e.target.checked})} />
                <span className={`text-sm ${isDark ? 'text-slate-300' : ''}`}>Enable reminders</span>
              </label>
            </div>
            <Button onClick={saveConfig} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="save-avail-config">
              Save Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ADD EVENT DIALOG */}
      <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Plus className="h-5 w-5 text-cyan-500" />Add Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Title</Label>
              <Input value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} placeholder="Team Practice" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Type</Label>
                <select value={newEvent.event_type} onChange={e => setNewEvent({...newEvent, event_type: e.target.value})} className={`w-full rounded-md border p-2 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="event-type-select">
                  <option value="practice">Practice</option>
                  <option value="meeting">Meeting</option>
                  <option value="fundraiser">Fundraiser</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Date</Label>
                <Input type="date" value={newEvent.date} onChange={e => setNewEvent({...newEvent, date: e.target.value})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="event-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Start Time</Label>
                <Input type="time" value={newEvent.start_time} onChange={e => setNewEvent({...newEvent, start_time: e.target.value})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="event-start-input" />
              </div>
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>End Time</Label>
                <Input type="time" value={newEvent.end_time} onChange={e => setNewEvent({...newEvent, end_time: e.target.value})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="event-end-input" />
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Location</Label>
              <Input value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} placeholder="Gym A" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="event-location-input" />
            </div>
            {/* Recurring toggle */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Repeating Event</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Create events on multiple days</p>
              </div>
              <Button size="sm" variant={isRecurring ? 'default' : 'outline'}
                className={isRecurring ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}
                onClick={() => setIsRecurring(!isRecurring)} data-testid="recurring-toggle">
                {isRecurring ? 'On' : 'Off'}
              </Button>
            </div>
            {isRecurring && (
              <div className="space-y-3">
                <div>
                  <Label className={isDark ? 'text-slate-300' : ''}>End Date</Label>
                  <Input type="date" value={recurringEnd} onChange={e => setRecurringEnd(e.target.value)}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="recurring-end-input" />
                </div>
                <div>
                  <Label className={isDark ? 'text-slate-300' : ''}>Days of Week</Label>
                  <div className="flex gap-1 mt-1">
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => (
                      <Button key={d} size="sm" type="button"
                        variant={recurringDays.includes(i) ? 'default' : 'outline'}
                        className={`px-2 h-7 text-xs ${recurringDays.includes(i) ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}`}
                        onClick={() => setRecurringDays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}
                        data-testid={`recurring-day-${d.toLowerCase()}`}
                      >{d}</Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {teams?.length > 1 && (
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Team</Label>
                <select value={newEvent.team_id} onChange={e => setNewEvent({...newEvent, team_id: e.target.value})} className={`w-full rounded-md border p-2 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="event-team-select">
                  <option value="">All Teams</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAddEvent(false)}>Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleAddEvent} disabled={addingEvent || !newEvent.title || !newEvent.date} data-testid="create-event-btn">
                {addingEvent ? 'Creating...' : 'Create Event'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
